import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    LinearProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import moment from 'moment';

const KpiReport: React.FC = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [isDownloading, setIsDownloading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

    const { data: kpiData, isLoading, error, refetch } = useQuery(
        ['analytics', 'kpi', startDate, endDate],
        async () => {
            const res = await apiClient.get('/api/analytics/kpi-data', {
                params: { startDate, endDate },
            });
            return res.data.data;
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
        }
    );

    React.useEffect(() => {
        refetch();
    }, [refetch]);

    // Создаёт задачу на фоновую генерацию KPI-отчёта.
    // Сервер сразу возвращает job_id, файл готовится в фоне ~3-30 сек.
    // Пользователь переходит на /reports чтобы скачать когда готово.
    const handleOrderReport = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            await apiClient.post('/api/reports/order', {
                type: 'kpi',
                params: { startDate, endDate },
            });
            setSnackbar({ open: true, message: 'Отчёт заказан. Откройте «Заказанные отчёты» чтобы скачать.' });
            setTimeout(() => navigate('/reports'), 800);
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Не удалось заказать отчёт';
            setSnackbar({ open: true, message: msg });
        } finally {
            setIsDownloading(false);
        }
    };

    const { operators, queues, stats, cancelStats, overallWait } = kpiData || {};

    const dates = useMemo(() => {
        const result: string[] = [];
        if (!startDate || !endDate) return result;

        const start = moment(startDate);
        const end = moment(endDate);
        for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
            result.push(m.format('YYYY-MM-DD'));
        }

        return result;
    }, [startDate, endDate]);

    const statsMap = useMemo(() => {
        const map = new Map<string, any>();
        if (!stats) return map;

        for (const s of stats) {
            const key = `${s.operator_id}|${s.queue_id}|${moment(s.date).format('YYYY-MM-DD')}`;
            map.set(key, s);
        }

        return map;
    }, [stats]);

    const cancelMap = useMemo(() => {
        const map = new Map<string, any>();
        if (!cancelStats) return map;

        for (const s of cancelStats) {
            const key = `${s.operator_id}|${moment(s.date).format('YYYY-MM-DD')}`;
            map.set(key, s);
        }

        return map;
    }, [cancelStats]);

    const overallWaitMap = useMemo(() => {
        const map = new Map<string, any>();
        if (!overallWait) return map;

        for (const s of overallWait) {
            map.set(moment(s.date).format('YYYY-MM-DD'), s);
        }

        return map;
    }, [overallWait]);

    if (error) {
        return <Alert severity="error">Ошибка загрузки данных: {(error as any).message}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    label="Дата начала"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <TextField
                    label="Дата окончания"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <Button variant="contained" onClick={() => refetch()} startIcon={<SearchIcon />}>
                    Показать
                </Button>
                <Button
                    variant="outlined"
                    color="success"
                    onClick={handleOrderReport}
                    startIcon={<FileDownloadIcon />}
                    disabled={!kpiData || isDownloading}
                >
                    {isDownloading ? 'Заказываем...' : 'Заказать Excel'}
                </Button>
                <Button variant="text" onClick={() => navigate('/reports')}>
                    Мои отчёты
                </Button>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ open: false, message: '' })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            {kpiData && operators && (
                <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', zIndex: 11 }}>
                                    Дата
                                </TableCell>
                                {operators.map((op: any) => (
                                    <TableCell
                                        key={op.id}
                                        colSpan={queues.length * 3 + 1 + 3}
                                        align="center"
                                        sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', borderLeft: '1px solid #ddd' }}
                                    >
                                        {op.last_name} {op.first_name}
                                    </TableCell>
                                ))}
                                <TableCell
                                    rowSpan={2}
                                    align="center"
                                    sx={{
                                        backgroundColor: '#fff8e1',
                                        fontWeight: 'bold',
                                        borderLeft: '2px solid #aaa',
                                        zIndex: 11,
                                        whiteSpace: 'normal',
                                        minWidth: 90,
                                    }}
                                >
                                    Ср.ожид.<br />клиента
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                {operators.map((op: any) => (
                                    <React.Fragment key={`queues-${op.id}`}>
                                        {queues.map((q: any) => (
                                            <TableCell
                                                key={q.id}
                                                colSpan={3}
                                                align="center"
                                                sx={{ backgroundColor: '#f5f5f5', borderLeft: '1px solid #ddd', fontSize: '0.75rem' }}
                                            >
                                                {q.name}
                                            </TableCell>
                                        ))}
                                        <TableCell
                                            align="center"
                                            sx={{ backgroundColor: '#ffebee', fontSize: '0.75rem', borderLeft: '1px solid #ddd' }}
                                        >
                                            Отмена
                                        </TableCell>
                                        <TableCell
                                            colSpan={3}
                                            align="center"
                                            sx={{
                                                backgroundColor: '#e8f5e9',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                                borderLeft: '2px solid #aaa',
                                            }}
                                        >
                                            Итого
                                        </TableCell>
                                    </React.Fragment>
                                ))}
                            </TableRow>

                            <TableRow>
                                {operators.map((op: any) => (
                                    <React.Fragment key={`metrics-${op.id}`}>
                                        {queues.map((q: any) => (
                                            <React.Fragment key={q.id}>
                                                <TableCell align="center" sx={{ minWidth: 50, borderLeft: '1px solid #ddd', fontSize: '0.7rem' }}>
                                                    Кол-во
                                                </TableCell>
                                                <TableCell align="center" sx={{ minWidth: 60, fontSize: '0.7rem' }}>
                                                    Обсл., мин
                                                </TableCell>
                                                <TableCell align="center" sx={{ minWidth: 60, fontSize: '0.7rem' }}>
                                                    Ожид., мин
                                                </TableCell>
                                            </React.Fragment>
                                        ))}
                                        <TableCell align="center" sx={{ minWidth: 50, borderLeft: '1px solid #ddd', fontSize: '0.7rem' }}>
                                            Кол-во
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                minWidth: 50,
                                                borderLeft: '2px solid #aaa',
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                                backgroundColor: '#e8f5e9',
                                            }}
                                        >
                                            Всего
                                        </TableCell>
                                        <TableCell align="center" sx={{ minWidth: 60, fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
                                            Ср.обсл.
                                        </TableCell>
                                        <TableCell align="center" sx={{ minWidth: 60, fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
                                            Ср.ожид.
                                        </TableCell>
                                    </React.Fragment>
                                ))}
                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 80,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        backgroundColor: '#fff8e1',
                                        borderLeft: '2px solid #aaa',
                                    }}
                                >
                                    мин
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dates.map((date) => (
                                <TableRow key={date} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{moment(date).format('DD.MM.YY')}</TableCell>
                                    {operators.map((op: any) => {
                                        let opDayCount = 0;
                                        let opDayServiceSum = 0;
                                        let opDayWaitSum = 0;

                                        return (
                                            <React.Fragment key={`${date}-${op.id}`}>
                                                {queues.map((q: any) => {
                                                    const stat = statsMap.get(`${op.id}|${q.id}|${date}`);

                                                    if (stat) {
                                                        const count = Number(stat.count || 0);
                                                        const serviceSum = Number(stat.sum_duration || Number(stat.avg_duration || 0) * count);
                                                        const waitSum = Number(stat.sum_wait_duration || Number(stat.avg_wait_duration || 0) * count);

                                                        opDayCount += count;
                                                        opDayServiceSum += serviceSum;
                                                        opDayWaitSum += waitSum;
                                                    }

                                                    return (
                                                        <React.Fragment key={`${date}-${op.id}-${q.id}`}>
                                                            <TableCell align="center" sx={{ borderLeft: '1px solid #eee' }}>
                                                                {stat ? stat.count : ''}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: 'text.secondary' }}>
                                                                {stat
                                                                    ? (Math.round((Number(stat.avg_duration || 0) / 60) * 100) / 100).toFixed(2)
                                                                    : ''}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: 'text.secondary' }}>
                                                                {stat
                                                                    ? (Math.round((Number(stat.avg_wait_duration || 0) / 60) * 100) / 100).toFixed(2)
                                                                    : ''}
                                                            </TableCell>
                                                        </React.Fragment>
                                                    );
                                                })}

                                                {(() => {
                                                    const cStat = cancelMap.get(`${op.id}|${date}`);
                                                    return (
                                                        <TableCell
                                                            align="center"
                                                            sx={{ borderLeft: '1px solid #eee', backgroundColor: cStat ? '#fff0f0' : 'inherit' }}
                                                        >
                                                            {cStat ? cStat.count : ''}
                                                        </TableCell>
                                                    );
                                                })()}

                                                <TableCell
                                                    align="center"
                                                    sx={{ borderLeft: '2px solid #aaa', fontWeight: 'bold', backgroundColor: '#f1f8e9' }}
                                                >
                                                    {opDayCount > 0 ? opDayCount : ''}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f1f8e9' }}>
                                                    {opDayCount > 0
                                                        ? (Math.round((opDayServiceSum / opDayCount / 60) * 100) / 100).toFixed(2)
                                                        : ''}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f1f8e9' }}>
                                                    {opDayCount > 0
                                                        ? (Math.round((opDayWaitSum / opDayCount / 60) * 100) / 100).toFixed(2)
                                                        : ''}
                                                </TableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                    {(() => {
                                        const w = overallWaitMap.get(date);
                                        const avgMin = w && w.count > 0
                                            ? (Math.round((Number(w.avg_wait_seconds || 0) / 60) * 100) / 100).toFixed(2)
                                            : '';
                                        return (
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#fff8e1',
                                                    borderLeft: '2px solid #aaa',
                                                }}
                                            >
                                                {avgMin}
                                            </TableCell>
                                        );
                                    })()}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default KpiReport;
